import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Linkedin, Calendar, Users, GraduationCap, AlertTriangle, ArrowRightLeft, KeyRound, Lock, Video, FileText, Bell, BellRing, AlertCircle, Download, Clock } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmContext";
import MentorAvailability from "@/components/MentorAvailability";

const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function formatLongDate(input) {
  if (!input) return "";
  let d;
  if (typeof input === "string") {
    const m = input.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    else d = new Date(input);
  } else d = new Date(input);
  if (Number.isNaN(d.getTime())) return String(input);
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const dom = d.getDate();
  const ord = (n) => (n >= 11 && n <= 13) ? "th" : ((n % 10) === 1 ? "st" : (n % 10) === 2 ? "nd" : (n % 10) === 3 ? "rd" : "th");
  return `${days[d.getDay()]} ${dom}${ord(dom)} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

function fmtHHMM(iso) { return iso ? iso.slice(11, 16) : ""; }
function isPast(iso) {
  if (!iso) return false;
  const dt = new Date(iso.endsWith("Z") || iso.includes("+") ? iso : iso + "Z");
  return !Number.isNaN(dt.getTime()) && dt.getTime() < Date.now();
}

export default function MentorDashboard() {
  const { user } = useAuth();
  const confirm = useConfirm();
  const [me, setMe] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [allSlots, setAllSlots] = useState([]);
  // Per-slot meeting link draft state (so user can edit before save)
  const [slotLinkDrafts, setSlotLinkDrafts] = useState({});
  const [savingSlot, setSavingSlot] = useState(null);
  const [allMentors, setAllMentors] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reassigning, setReassigning] = useState(null);
  const [noteEditing, setNoteEditing] = useState(null);
  const [noteText, setNoteText] = useState("");
  // Meeting link / recording / no-show ephemeral state
  const [linkEditing, setLinkEditing] = useState(null);
  const [linkText, setLinkText] = useState("");
  const [recEditing, setRecEditing] = useState(null);
  const [recForm, setRecForm] = useState({ recording_url: "", recording_summary: "" });
  // Notifications
  const [notifications, setNotifications] = useState([]);
  // Change password
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const [pwError, setPwError] = useState("");
  // Apply default link to all my slots
  const [applyingAll, setApplyingAll] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/mentor/me");
      setMe(data.mentor);
      setBookings(data.bookings || []);
      setAllSlots(data.all_slots || []);
      setForm({
        bio: data.mentor.bio || "",
        photo_url: data.mentor.photo_url || "",
        expertise: data.mentor.expertise || "",
        availability: data.mentor.availability || "",
        linkedin_url: data.mentor.linkedin_url || "",
        default_meeting_link: data.mentor.default_meeting_link || "",
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load mentor profile");
    }
    try {
      const { data: ms } = await api.get("/mentors");
      setAllMentors(ms || []);
    } catch (e) { /* ok */ }
    try {
      const { data: nots } = await api.get("/mentor/notifications");
      setNotifications(nots || []);
    } catch (e) { /* ok */ }
  };

  useEffect(() => { load(); }, []);
  // If the user lands with #mentor-bookings-section in the URL, scroll to it
  // once data has loaded. Re-runs on hash changes too.
  useEffect(() => {
    if (!me || !bookings) return;
    if (typeof window === "undefined") return;
    if (window.location.hash === "#mentor-bookings-section") {
      const el = document.getElementById("mentor-bookings-section");
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
    }
  }, [me, bookings]);
  // Poll notifications every 30s for near-real-time
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const { data } = await api.get("/mentor/notifications");
        setNotifications(data || []);
      } catch {}
    }, 30000);
    return () => clearInterval(id);
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/mentor/me", form);
      toast.success("Profile updated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const applyDefaultToAll = async () => {
    if (!me?.id) return;
    const currentDefault = (form?.default_meeting_link || "").trim();
    if (!currentDefault) {
      toast.error("Add a default meeting link above, save your profile, then apply.");
      return;
    }
    if ((me.default_meeting_link || "").trim() !== currentDefault) {
      if (!(await confirm({
        title: "Save profile first?",
        description: "The default link in this form is different from what's saved. Save your profile, then click Apply again.",
        confirmText: "OK",
      }))) return;
      return;
    }
    if (!(await confirm({
      title: "Apply your default link to every one of your slots?",
      description: "This sets the meeting link on every booked + available slot you own to your default. Admin can still override an individual slot afterwards.",
      confirmText: "Apply to all my slots",
    }))) return;
    setApplyingAll(true);
    try {
      const { data } = await api.post(`/mentors/${me.id}/apply-default-link-to-slots`);
      toast.success(`${data.updated} slot${data.updated === 1 ? "" : "s"} updated`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not apply");
    } finally {
      setApplyingAll(false);
    }
  };

  const reassign = async (slotId, toMentorId) => {
    if (!toMentorId) return;
    try {
      await api.post(`/mentor-slots/${slotId}/reassign`, { to_mentor_id: toMentorId });
      toast.success("Booking moved to alternative mentor");
      setReassigning(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reassign");
    }
  };

  const saveNote = async (slotId) => {
    if (!noteText.trim()) return toast.error("Note can't be empty");
    try {
      await api.post(`/mentor-slots/${slotId}/note`, { note: noteText });
      toast.success("Session note saved");
      setNoteEditing(null);
      setNoteText("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save note");
    }
  };

  const startEditNote = (booking) => {
    setNoteEditing(booking.id);
    setNoteText(booking.session_note || "");
  };

  const saveLink = async (slotId) => {
    try {
      await api.put(`/mentor-slots/${slotId}/meeting-link`, { meeting_link: linkText });
      toast.success("Meeting link updated");
      setLinkEditing(null);
      setLinkText("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save link");
    }
  };

  const toggleNoShow = async (booking) => {
    try {
      await api.put(`/mentor-slots/${booking.id}/no-show`, { no_show: !booking.no_show });
      toast.success(booking.no_show ? "No-show flag cleared" : "Marked as no-show");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    }
  };

  const releaseEarly = async (booking) => {
    if (!(await confirm({
      title: `Release "${booking.slot_start} – ${booking.slot_end}" early?`,
      description: booking.reserve_by_team_id
        ? `The reserve team (${booking.reserve_team_name || "—"}) will be auto-promoted, get an in-app notification, and the meeting link will appear on their dashboard. You don't need to do anything else.`
        : "No reserve team is waiting. The slot will be flagged as released-early in the records.",
      confirmText: "Yes, release early",
    }))) return;
    try {
      await api.post(`/mentor-slots/${booking.id}/release-early`);
      toast.success(booking.reserve_by_team_id ? "Reserve team promoted and notified" : "Marked as released early");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not release");
    }
  };

  const swapLateWithReserve = async (booking) => {
    if (!(await confirm({
      title: `Swap late team with reserve?`,
      description: `${booking.reserve_team_name || "The reserve team"} will be promoted to the primary slot and get the meeting link. ${booking.team_name || "The original team"} will become the reserve and receive a courtesy notification.`,
      confirmText: "Yes, swap them",
      destructive: true,
    }))) return;
    try {
      await api.post(`/mentor-slots/${booking.id}/swap-late-with-reserve`);
      toast.success("Swapped — reserve team promoted and notified");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not swap");
    }
  };

  const saveRecording = async (slotId) => {
    try {
      await api.put(`/mentor-slots/${slotId}/recording`, recForm);
      toast.success("Recording saved");
      setRecEditing(null);
      setRecForm({ recording_url: "", recording_summary: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save recording");
    }
  };

  const startEditRecording = (b) => {
    setRecEditing(b.id);
    setRecForm({ recording_url: b.recording_url || "", recording_summary: b.recording_summary || "" });
  };

  const dismissNotification = async (id) => {
    try { await api.post(`/mentor/notifications/${id}/read`); }
    catch {}
    setNotifications((n) => n.map((x) => x.id === id ? { ...x, read_at: new Date().toISOString() } : x));
  };

  const markAllRead = async () => {
    try { await api.post("/mentor/notifications/read-all"); load(); }
    catch (e) { toast.error("Could not mark all read"); }
  };

  const downloadIcs = (slotId) => {
    const token = localStorage.getItem("token") || "";
    fetch(`${REACT_APP_BACKEND_URL}/api/mentor-slots/${slotId}/ics`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `mentor-session-${slotId.slice(0, 8)}.ics`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Could not download calendar invite"));
  };

  const changePassword = async () => {
    setPwError("");
    if (!pwForm.current || !pwForm.next) return setPwError("Fill in current and new passwords");
    if (pwForm.next.length < 8) return setPwError("New password must be at least 8 characters");
    if (pwForm.next !== pwForm.confirm) return setPwError("New password and confirmation don't match");
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pwForm.current, new_password: pwForm.next });
      toast.success("Password updated");
      setPwForm({ current: "", next: "", confirm: "" });
    } catch (e) {
      setPwError(e?.response?.data?.detail || "Could not change password");
    } finally { setPwBusy(false); }
  };

  const saveSlotLink = async (slotId, link) => {
    setSavingSlot(slotId);
    try {
      await api.put(`/mentor-slots/${slotId}/meeting-link`, { meeting_link: (link || "").trim() || null });
      toast.success("Meeting link saved");
      setSlotLinkDrafts((d) => { const n = { ...d }; delete n[slotId]; return n; });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save link");
    } finally { setSavingSlot(null); }
  };

  // Group ALL my slots by day for the per-slot link manager
  const slotsByDay = useMemo(() => {
    const map = {};
    allSlots.forEach((s) => {
      const k = s.day_date || "—";
      if (!map[k]) map[k] = { day_date: s.day_date, slots: [] };
      map[k].slots.push(s);
    });
    return Object.values(map).sort((a, b) => (a.day_date || "").localeCompare(b.day_date || ""));
  }, [allSlots]);

  // Group bookings by day_date and classify past/no-show/future
  const groupedBookings = useMemo(() => {
    const map = {};
    bookings.forEach((b) => {
      const key = b.day_date || "—";
      if (!map[key]) map[key] = { day_date: b.day_date, items: [] };
      map[key].items.push(b);
    });
    return Object.values(map).sort((a, b) => (a.day_date || "").localeCompare(b.day_date || ""));
  }, [bookings]);

  const unreadCount = notifications.filter((n) => !n.read_at).length;

  if (!me || !form) return <div className="text-sm text-[#525252]">Loading…</div>;

  const otherMentors = allMentors.filter((m) => m.id !== me.id);

  return (
    <div className="space-y-8" data-testid="mentor-dashboard-page">
      <div>
        <div className="label-eyebrow flex items-center gap-2"><GraduationCap size={12} /> Mentor area</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2">Welcome, {me.name}</h1>
        <p className="text-[#525252] mt-2 max-w-2xl text-sm">
          One place for your week: bookings grouped by day, meeting links, session notes, recordings, and no-shows.
          Link any session to your own calendar with the .ics download per booking.
        </p>
      </div>

      {/* No-link warning banner — visible when mentor hasn't set a default link */}
      {!(me?.default_meeting_link || "").trim() && (
        <section
          className="border-2 border-[#f4b847] bg-[#FEF8E1] p-4 md:p-5 flex items-start gap-4"
          data-testid="mentor-no-default-link-banner"
        >
          <div className="shrink-0 w-10 h-10 bg-[#f4b847] text-[#0A0A0A] flex items-center justify-center">
            <AlertTriangle size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="label-eyebrow text-[#7A5B17]">Action needed · 30 seconds</div>
            <h3 className="font-display text-xl md:text-2xl font-black tracking-tighter mt-1 text-[#0A0A0A]">
              Add your default meeting link
            </h3>
            <p className="text-sm text-[#0A0A0A] mt-2 leading-relaxed max-w-2xl">
              Without it, teams who book you will see <strong>&quot;No link yet&quot;</strong> and won&apos;t know where to join.
              Scroll to the cyan <em>Default meeting link</em> panel below, paste your standing Zoom / Meet / Teams URL,
              hit <em>Save profile</em>, then click <em>Apply to all my slots</em>.
            </p>
            <button
              onClick={() => {
                const el = document.querySelector('[data-testid="mentor-default-link-panel"]');
                if (el) {
                  el.scrollIntoView({ behavior: "smooth", block: "center" });
                  const input = el.querySelector('input');
                  if (input) setTimeout(() => input.focus(), 600);
                }
              }}
              className="inline-flex items-center gap-2 bg-[#0A0A0A] text-[#f4b847] px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-[#1a1a1a] transition-colors mt-3"
              data-testid="mentor-no-link-banner-jump"
            >
              Take me there →
            </button>
          </div>
        </section>
      )}

      {/* Mentor user guide — prominent, always visible */}
      <section
        className="border-2 border-[#0A0A0A] bg-[#FEF3D0] p-5 md:p-6"
        data-testid="mentor-user-guide-card"
      >
        <div className="flex flex-col md:flex-row md:items-start gap-5">
          <div className="shrink-0 w-12 h-12 bg-[#0A0A0A] text-[#f4b847] flex items-center justify-center">
            <FileText size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="label-eyebrow text-[#7A5B17]">Mentor user guide · read me first</div>
            <h2 className="font-display text-2xl md:text-3xl font-black tracking-tighter mt-1 text-[#0A0A0A]">
              How to use the Mentor dashboard
            </h2>
            <ul className="mt-3 text-sm text-[#0A0A0A] leading-relaxed space-y-1.5 list-disc pl-5">
              <li><strong>My availability</strong> — publish the times you can offer between 5 Nov and 8 Dec; your team books from these. 8 hours online in total, split evenly if you have two teams.</li>
              <li><strong>My Bookings</strong> — every slot your team has booked, grouped by day. Add a meeting link, leave a session note, or mark a no-show.</li>
              <li><strong>Default meeting link</strong> — set it once in your profile and every slot uses it; override per-slot below if needed.</li>
              <li><strong>Release early</strong> — finished ahead of time? Click and the reserve team is auto-promoted with the link.</li>
              <li><strong>Swap late with reserve</strong> — primary team hasn't shown up? Click to promote the reserve and demote the late team.</li>
              <li><strong>Calendar invite (.ics)</strong> — download per booking to drop into your own calendar.</li>
              <li><strong>Teams&apos; Progress</strong> — read-only view of every team&apos;s workbook, business and challenge statement so you can prep before a call.</li>
            </ul>
            <div className="mt-4 flex flex-wrap gap-2">
              <a
                href="/guide/mentors.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#0A0A0A] text-[#f4b847] px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-[#1a1a1a] transition-colors"
                data-testid="mentor-guide-pdf-link"
              >
                <FileText size={14} /> Open the Mentor Guide (PDF)
              </a>
              <a
                href="https://www.dropbox.com/scl/fi/a7rn9337xx8fid3cc5cfb/Day-1-Get-the-Best-from-the-Mentors.pdf?rlkey=cm9r6i46sks5qg3dlrzjd82fq&dl=0"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border border-[#0A0A0A] px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-[#0A0A0A] hover:text-white transition-colors"
                data-testid="mentor-best-from-mentors-pdf-link"
              >
                <FileText size={14} /> Get the best from mentors (PDF)
              </a>
              <a
                href="#mentor-bookings-section"
                className="inline-flex items-center gap-2 border border-[#0A0A0A] px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-[#0A0A0A] hover:text-white transition-colors"
                data-testid="mentor-guide-jump-bookings"
              >
                <Calendar size={14} /> Jump to my bookings
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Notifications strip */}
      {notifications.length > 0 && (
        <div className="border border-[#E5E7EB] bg-white p-5" data-testid="mentor-notifications-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="label-eyebrow flex items-center gap-2">
              {unreadCount > 0 ? <BellRing size={12} className="text-[#A23232]" /> : <Bell size={12} />}
              Booking notifications {unreadCount > 0 && <span className="bg-[#A23232] text-white text-[10px] px-2 py-0.5 ml-1">{unreadCount} new</span>}
            </div>
            {unreadCount > 0 && (
              <button onClick={markAllRead} className="text-[11px] uppercase tracking-wider text-[#2596be] hover:underline" data-testid="mentor-notif-mark-all-read">
                Mark all read
              </button>
            )}
          </div>
          <ul className="space-y-2 max-h-60 overflow-y-auto pr-1">
            {notifications.slice(0, 10).map((n) => (
              <li
                key={n.id}
                className={`text-xs border p-3 flex items-start justify-between gap-3 ${n.read_at ? "border-[#E5E7EB] bg-[#FAFAFA]" : "border-[#2596be] bg-[#E6F4FA]"}`}
                data-testid={`mentor-notification-${n.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-[#525252]">{new Date(n.created_at).toLocaleString()}</div>
                  <div className="mt-1 text-[#0A0A0A] whitespace-pre-wrap">{n.message}</div>
                </div>
                {!n.read_at && (
                  <button onClick={() => dismissNotification(n.id)} className="text-[10px] uppercase tracking-wider text-[#525252] hover:text-[#0A0A0A]" data-testid={`mentor-notif-dismiss-${n.id}`}>
                    Mark read
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Edit profile */}
        <div className="lg:col-span-2 border border-[#0A0A0A] bg-white p-6 space-y-4" data-testid="mentor-edit-panel">
          <div className="label-eyebrow">Edit my profile</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Photo URL" value={form.photo_url} onChange={(v) => setForm((f) => ({ ...f, photo_url: v }))} placeholder="https://…" testid="mentor-photo-input" />
            <Field label="LinkedIn URL" value={form.linkedin_url} onChange={(v) => setForm((f) => ({ ...f, linkedin_url: v }))} placeholder="https://www.linkedin.com/in/…" testid="mentor-linkedin-input" />
          </div>
          <Field label="Expertise" value={form.expertise} onChange={(v) => setForm((f) => ({ ...f, expertise: v }))} placeholder="e.g. Post-merger integration, founder coaching" testid="mentor-expertise-input" />
          <div>
            <Label className="label-eyebrow">Availability — free-text note (optional)</Label>
            <Input
              className="mt-2 rounded-none"
              value={form.availability}
              onChange={(e) => setForm((f) => ({ ...f, availability: e.target.value }))}
              placeholder="e.g. Day 1 & 2 only, video preferred"
              data-testid="mentor-availability-input"
            />
            <p className="text-[10px] text-[#525252] mt-1.5 leading-relaxed">
              This is a free-text note shown on your public mentor card — useful for context like "video calls only"
              or "prefer mornings". <strong>Your actual bookable slots are managed by admin</strong> on the Mentor
              Slot Manager — students can only book slots admin has allocated to you.
            </p>
          </div>
          <div className="border border-[#2596be] bg-[#E6F4FA] p-3" data-testid="mentor-default-link-panel">
            <Field
              label="Default meeting link (Zoom / Google Meet / Teams)"
              value={form.default_meeting_link}
              onChange={(v) => setForm((f) => ({ ...f, default_meeting_link: v }))}
              placeholder="https://… — used for every slot unless admin overrides a specific one"
              testid="mentor-default-meeting-link-input"
            />
            <p className="text-[11px] text-[#0A0A0A] mt-2 leading-relaxed">
              Paste your standing meeting room here. It will be used by default for every slot you&apos;re booked into.
              You or admin can still override a specific slot below (or via the Mentor Slot Manager).
              <strong> Save profile first</strong>, then click <em>Apply to all my slots</em> if you want the link
              written explicitly onto every existing slot.
            </p>
            <Button
              onClick={applyDefaultToAll}
              disabled={applyingAll || !form.default_meeting_link?.trim()}
              variant="outline"
              className="h-8 rounded-none border-[#2596be] text-[#2596be] hover:bg-white text-[11px] uppercase tracking-wide mt-3"
              data-testid="mentor-apply-default-link-btn"
            >
              {applyingAll ? "Applying…" : "Apply to all my slots"}
            </Button>
          </div>
          <div>
            <Label className="label-eyebrow">Bio</Label>
            <Textarea
              className="mt-2 rounded-none min-h-[160px]"
              value={form.bio}
              onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))}
              placeholder="Tell teams who you are, what you've done, how you like to work…"
              data-testid="mentor-bio-input"
            />
          </div>
          <div className="flex items-center justify-between">
            {form.photo_url && (
              <img src={form.photo_url} alt={me.name} className="h-16 w-16 object-cover border border-[#E5E7EB]" />
            )}
            <Button onClick={save} disabled={saving} className="rounded-none bg-[#2596be] hover:bg-[#1e7da3]" data-testid="mentor-save-btn">
              {saving ? "Saving…" : "Save profile"}
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="border-2 border-[#0A0A0A] bg-white p-6 space-y-4" data-testid="mentor-stats-panel">
          <div className="label-eyebrow flex items-center gap-2"><Users size={12} /> My week at a glance</div>
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Total bookings" value={bookings.length} testid="mentor-stat-total" />
            <Stat label="Upcoming" value={bookings.filter((b) => !isPast(b.slot_start) && !b.no_show).length} testid="mentor-stat-upcoming" />
            <Stat label="Completed" value={bookings.filter((b) => isPast(b.slot_start) && !b.no_show).length} testid="mentor-stat-completed" />
            <Stat label="No-shows" value={bookings.filter((b) => b.no_show).length} testid="mentor-stat-noshows" tone="red" />
          </div>
          <p className="text-xs text-[#525252] mt-2">
            Your team books from the availability you publish below. Online hours run 5 Nov – 8 Dec.
          </p>
        </div>
      </div>

      <MentorAvailability mentorId={me.id} slots={allSlots} onChanged={load} />

      {/* Per-slot meeting link manager */}
      {allSlots.length > 0 && (
        <section className="border border-[#E5E7EB] bg-white p-6" data-testid="mentor-slot-links-panel">
          <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
            <div>
              <div className="label-eyebrow flex items-center gap-2"><Video size={12} /> Meeting links per session</div>
              <h2 className="font-display text-2xl font-black tracking-tighter mt-1">Override links for specific slots</h2>
              <p className="text-xs text-[#525252] mt-2 max-w-xl leading-relaxed">
                Your <strong>default meeting link</strong> (above) is used for every slot. If a particular session needs
                a different link — e.g. an evening session on a personal account vs an afternoon one on a shared
                room — set it here and it will override the default for that slot only.
              </p>
            </div>
          </div>
          {!me.default_meeting_link && (
            <div className="mb-4 border border-[#f4b847] bg-[#FEF3D0] p-3 text-xs text-[#7A5B17]" data-testid="mentor-no-default-link-banner">
              <strong>No default link set yet.</strong> Add one in the profile above so every empty slot below auto-uses it.
            </div>
          )}
          <div className="space-y-5">
            {slotsByDay.map((g) => (
              <div key={g.day_date} data-testid={`mentor-slot-links-day-${g.day_date}`}>
                <div className="font-display text-base font-bold tracking-tight mb-2">{formatLongDate(g.day_date)}</div>
                <ul className="space-y-2">
                  {g.slots.map((s) => {
                    const draft = slotLinkDrafts[s.id];
                    const current = s.meeting_link || "";
                    const value = draft !== undefined ? draft : current;
                    const usingDefault = !current && !!me.default_meeting_link;
                    const past = isPast(s.slot_start);
                    return (
                      <li
                        key={s.id}
                        className={`border p-3 grid grid-cols-1 md:grid-cols-[200px_1fr_auto] gap-3 items-center ${past ? "border-[#E5E7EB] bg-[#FAFAFA]" : "border-[#E5E7EB] bg-white"}`}
                        data-testid={`mentor-slot-link-row-${s.id}`}
                      >
                        <div className="text-xs">
                          <div className="font-semibold inline-flex items-center gap-1">
                            <Clock size={11} /> {fmtHHMM(s.slot_start)}–{fmtHHMM(s.slot_end)}
                          </div>
                          {s.booked_by_team_id ? (
                            <div className="text-[10px] text-[#1e7da3] mt-0.5 inline-flex items-center gap-1">
                              <Users size={9} /> {s.team_name || "Booked"}
                            </div>
                          ) : (
                            <div className="text-[10px] text-[#525252] mt-0.5">Open</div>
                          )}
                          {past && <div className="text-[10px] text-[#525252] mt-0.5">Past</div>}
                        </div>
                        <Input
                          value={value}
                          onChange={(e) => setSlotLinkDrafts((d) => ({ ...d, [s.id]: e.target.value }))}
                          placeholder={usingDefault ? `(Using default: ${me.default_meeting_link})` : "https://… (per-slot override)"}
                          className="rounded-none text-xs"
                          data-testid={`mentor-slot-link-input-${s.id}`}
                        />
                        <div className="flex items-center gap-2 justify-end">
                          {value !== current && (
                            <Button
                              onClick={() => saveSlotLink(s.id, value)}
                              disabled={savingSlot === s.id}
                              className="rounded-none h-8 text-xs bg-[#2596be] hover:bg-[#1e7da3]"
                              data-testid={`mentor-slot-link-save-${s.id}`}
                            >
                              {savingSlot === s.id ? "Saving…" : "Save"}
                            </Button>
                          )}
                          {current && value === current && (
                            <Button
                              onClick={() => saveSlotLink(s.id, "")}
                              variant="outline"
                              className="rounded-none h-8 text-xs border-[#A23232] text-[#A23232]"
                              data-testid={`mentor-slot-link-clear-${s.id}`}
                            >
                              Clear
                            </Button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Grouped bookings */}
      <section id="mentor-bookings-section" data-testid="mentor-bookings-grouped">
        <div className="label-eyebrow flex items-center gap-2 mb-3"><Calendar size={12} /> Bookings — by day</div>
        {groupedBookings.length === 0 ? (
          <div className="border border-dashed border-[#E5E7EB] p-6 text-center text-sm text-[#525252]" data-testid="mentor-bookings-empty">
            No bookings yet. Teams will start to book as Day 1 approaches.
          </div>
        ) : (
          <div className="space-y-6">
            {groupedBookings.map((g) => (
              <div key={g.day_date} data-testid={`mentor-bookings-day-${g.day_date}`}>
                <div className="font-display text-lg font-bold tracking-tight mb-2">{formatLongDate(g.day_date)}</div>
                <ul className="space-y-3">
                  {g.items.sort((a, b) => (a.slot_start || "").localeCompare(b.slot_start || "")).map((b) => {
                    const past = isPast(b.slot_start);
                    return (
                      <li
                        key={b.id}
                        className={`border p-4 space-y-3 ${b.no_show ? "border-[#A23232] bg-[#FDEDED]" : past ? "border-[#E5E7EB] bg-[#FAFAFA]" : "border-[#2596be] bg-white"}`}
                        data-testid={`mentor-booking-${b.id}`}
                      >
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <div>
                            <div className="text-sm font-semibold">
                              {b.team_name || "Unknown team"}
                              <span className="ml-2 text-xs text-[#525252] inline-flex items-center gap-1">
                                <Clock size={11} /> {fmtHHMM(b.slot_start)}–{fmtHHMM(b.slot_end)}
                              </span>
                            </div>
                            {b.booking_notes && (
                              <div className="text-xs text-[#525252] italic mt-1">"{b.booking_notes}"</div>
                            )}
                            {b.reserve_by_team_id && (
                              <div className="text-[10px] uppercase tracking-wider text-[#7A5B17] mt-1 inline-flex items-center gap-1" data-testid={`mentor-booking-reserve-${b.id}`}>
                                <Clock size={10} /> Reserve waiting: <strong className="ml-1">{b.reserve_team_name || "—"}</strong>
                              </div>
                            )}
                            {b.early_released_at && (
                              <div className="text-[10px] uppercase tracking-wider text-[#1e7da3] mt-1">Released early</div>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            {b.no_show && (
                              <span className="text-[10px] uppercase tracking-wider bg-[#A23232] text-white px-2 py-1 inline-flex items-center gap-1" data-testid={`mentor-booking-noshow-flag-${b.id}`}>
                                <AlertCircle size={11} /> No-show
                              </span>
                            )}
                            {past && !b.no_show && (
                              <span className="text-[10px] uppercase tracking-wider bg-[#E5E7EB] text-[#525252] px-2 py-1">Past</span>
                            )}
                            <button
                              onClick={() => downloadIcs(b.id)}
                              className="text-[11px] uppercase tracking-wider text-[#2596be] hover:underline inline-flex items-center gap-1"
                              data-testid={`mentor-booking-ics-${b.id}`}
                            >
                              <Download size={11} /> Add to calendar
                            </button>
                          </div>
                        </div>

                        {/* Meeting link */}
                        <div className="border-t border-[#E5E7EB] pt-2" data-testid={`mentor-booking-link-${b.id}`}>
                          {linkEditing === b.id ? (
                            <div className="flex flex-col md:flex-row gap-2">
                              <Input value={linkText} onChange={(e) => setLinkText(e.target.value)} placeholder="https://…" className="rounded-none flex-1 h-8 text-xs" data-testid={`mentor-link-input-${b.id}`} />
                              <Button onClick={() => saveLink(b.id)} className="rounded-none h-8 text-xs bg-[#2596be] hover:bg-[#1e7da3]" data-testid={`mentor-link-save-${b.id}`}>Save link</Button>
                              <Button onClick={() => { setLinkEditing(null); setLinkText(""); }} variant="outline" className="rounded-none h-8 text-xs" data-testid={`mentor-link-cancel-${b.id}`}>Cancel</Button>
                            </div>
                          ) : (b.meeting_link || me.default_meeting_link) ? (
                            <div className="text-xs flex items-center justify-between flex-wrap gap-2">
                              <a href={b.meeting_link || me.default_meeting_link} target="_blank" rel="noopener noreferrer" className="text-[#2596be] hover:text-[#1e7da3] font-semibold inline-flex items-center gap-1" data-testid={`mentor-link-open-${b.id}`}>
                                <Video size={11} /> {b.meeting_link ? "Per-slot link" : "Default link"} →
                              </a>
                              <button onClick={() => { setLinkEditing(b.id); setLinkText(b.meeting_link || ""); }} className="text-[#525252] hover:underline" data-testid={`mentor-link-edit-${b.id}`}>
                                Edit
                              </button>
                            </div>
                          ) : (
                            <button onClick={() => { setLinkEditing(b.id); setLinkText(""); }} className="text-xs text-[#1e7da3] hover:underline inline-flex items-center gap-1" data-testid={`mentor-link-add-${b.id}`}>
                              <Video size={11} /> + Add meeting link
                            </button>
                          )}
                        </div>

                        {/* Session note */}
                        <div className="border-t border-[#E5E7EB] pt-2" data-testid={`mentor-booking-note-${b.id}`}>
                          {b.session_note && noteEditing !== b.id && (
                            <div className="bg-[#FEF3D0] border-l-2 border-[#f4b847] p-2 text-xs text-[#0A0A0A] whitespace-pre-wrap mb-2" data-testid={`mentor-booking-note-text-${b.id}`}>
                              <div className="label-eyebrow text-[#7A5B17] text-[9px] mb-1">Session note (visible to the team)</div>
                              {b.session_note}
                            </div>
                          )}
                          {noteEditing === b.id ? (
                            <div className="space-y-2">
                              <Textarea className="rounded-none min-h-[80px] text-xs" placeholder="What was discussed? Recommended next steps?" value={noteText} onChange={(e) => setNoteText(e.target.value)} data-testid={`mentor-booking-note-input-${b.id}`} />
                              <div className="flex gap-2">
                                <Button onClick={() => saveNote(b.id)} className="rounded-none h-7 text-xs bg-[#2596be] hover:bg-[#1e7da3]" data-testid={`mentor-booking-note-save-${b.id}`}>Save note</Button>
                                <Button onClick={() => { setNoteEditing(null); setNoteText(""); }} variant="outline" className="rounded-none h-7 text-xs" data-testid={`mentor-booking-note-cancel-${b.id}`}>Cancel</Button>
                              </div>
                            </div>
                          ) : (
                            <button onClick={() => startEditNote(b)} className="text-xs text-[#1e7da3] hover:underline" data-testid={`mentor-booking-note-edit-${b.id}`}>
                              {b.session_note ? "Edit session note" : "+ Add session note"}
                            </button>
                          )}
                        </div>

                        {/* Recording */}
                        <div className="border-t border-[#E5E7EB] pt-2" data-testid={`mentor-booking-recording-${b.id}`}>
                          {recEditing === b.id ? (
                            <div className="space-y-2">
                              <Input value={recForm.recording_url} onChange={(e) => setRecForm((f) => ({ ...f, recording_url: e.target.value }))} placeholder="Recording URL (Loom / Drive / Dropbox)" className="rounded-none h-8 text-xs" data-testid={`mentor-rec-url-${b.id}`} />
                              <Textarea value={recForm.recording_summary} onChange={(e) => setRecForm((f) => ({ ...f, recording_summary: e.target.value }))} placeholder="One-paragraph summary of the session…" className="rounded-none min-h-[80px] text-xs" data-testid={`mentor-rec-summary-${b.id}`} />
                              <div className="flex gap-2">
                                <Button onClick={() => saveRecording(b.id)} className="rounded-none h-7 text-xs bg-[#2596be] hover:bg-[#1e7da3]" data-testid={`mentor-rec-save-${b.id}`}>Save</Button>
                                <Button onClick={() => setRecEditing(null)} variant="outline" className="rounded-none h-7 text-xs" data-testid={`mentor-rec-cancel-${b.id}`}>Cancel</Button>
                              </div>
                            </div>
                          ) : (b.recording_url || b.recording_summary) ? (
                            <div className="bg-[#E6F4FA] border-l-2 border-[#2596be] p-2 text-xs">
                              <div className="label-eyebrow text-[#1e7da3] text-[9px] mb-1 flex items-center gap-1"><FileText size={10} /> Recording / summary</div>
                              {b.recording_summary && <div className="whitespace-pre-wrap text-[#0A0A0A] mb-1">{b.recording_summary}</div>}
                              {b.recording_url && <a href={b.recording_url} target="_blank" rel="noopener noreferrer" className="text-[#2596be] font-semibold">Open recording →</a>}
                              <button onClick={() => startEditRecording(b)} className="block text-[#525252] hover:underline mt-1 text-[10px]" data-testid={`mentor-rec-edit-${b.id}`}>Edit</button>
                            </div>
                          ) : past ? (
                            <button onClick={() => startEditRecording(b)} className="text-xs text-[#1e7da3] hover:underline inline-flex items-center gap-1" data-testid={`mentor-rec-add-${b.id}`}>
                              <FileText size={11} /> + Add recording / summary
                            </button>
                          ) : null}
                        </div>

                        {/* Actions row */}
                        <div className="border-t border-[#E5E7EB] pt-2 flex flex-wrap items-center gap-3">
                          {!past && !b.early_released_at && (
                            <button
                              onClick={() => releaseEarly(b)}
                              className="text-[11px] uppercase tracking-wider text-[#1e7da3] hover:underline inline-flex items-center gap-1"
                              title={b.reserve_by_team_id ? "Promotes the reserve team into your slot" : "Marks the slot as released early"}
                              data-testid={`mentor-release-early-${b.id}`}
                            >
                              <Clock size={11} /> Release early{b.reserve_by_team_id ? " → promote reserve" : ""}
                            </button>
                          )}
                          {!past && b.reserve_by_team_id && (
                            <button
                              onClick={() => swapLateWithReserve(b)}
                              className="text-[11px] uppercase tracking-wider text-[#7A5B17] hover:underline inline-flex items-center gap-1"
                              title="If primary team is 5+ min late, swap them with the reserve"
                              data-testid={`mentor-swap-late-${b.id}`}
                            >
                              <ArrowRightLeft size={11} /> Swap late team with reserve
                            </button>
                          )}
                          {past && (
                            <button onClick={() => toggleNoShow(b)} className={`text-[11px] uppercase tracking-wider inline-flex items-center gap-1 ${b.no_show ? "text-[#525252]" : "text-[#A23232] hover:text-[#7A1F1F]"}`} data-testid={`mentor-noshow-toggle-${b.id}`}>
                              <AlertCircle size={11} /> {b.no_show ? "Clear no-show" : "Mark no-show"}
                            </button>
                          )}
                          {reassigning === b.id ? (
                            <div className="flex items-center gap-2 flex-wrap">
                              <select className="text-xs border border-[#0A0A0A] px-2 py-1" onChange={(e) => reassign(b.id, e.target.value)} defaultValue="" data-testid={`mentor-reassign-select-${b.id}`}>
                                <option value="" disabled>Choose mentor…</option>
                                {otherMentors.map((m) => (<option key={m.id} value={m.id}>{m.name}</option>))}
                              </select>
                              <button onClick={() => setReassigning(null)} className="text-xs text-[#525252] underline" data-testid={`mentor-reassign-cancel-${b.id}`}>cancel</button>
                            </div>
                          ) : !past ? (
                            <button onClick={() => setReassigning(b.id)} className="text-[11px] uppercase tracking-wider text-[#1e7da3] hover:underline inline-flex items-center gap-1" data-testid={`mentor-reassign-btn-${b.id}`}>
                              <ArrowRightLeft size={11} /> Reassign
                            </button>
                          ) : null}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        )}
        <div className="mt-4 text-[10px] uppercase tracking-wider text-[#525252] inline-flex items-center gap-1">
          <AlertTriangle size={11} /> Availability changes — please reassign affected bookings
        </div>
      </section>

      {/* Change password */}
      <div className="border border-[#E5E7EB] bg-white p-6 max-w-2xl" data-testid="mentor-change-password-panel">
        <div className="label-eyebrow flex items-center gap-2"><KeyRound size={12} /> Change my password</div>
        <h2 className="font-display text-2xl font-black tracking-tighter mt-1">Set your own password</h2>
        <p className="text-xs text-[#525252] mt-2 max-w-md leading-relaxed">
          On first login your account uses the default password <code className="bg-[#F3F4F6] px-1.5 py-0.5">MentorWeave2026!</code> —
          please change it to something memorable to you. Minimum 8 characters.
        </p>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="label-eyebrow">Current password</Label>
            <Input
              type="password"
              value={pwForm.current}
              onChange={(e) => setPwForm((f) => ({ ...f, current: e.target.value }))}
              className="rounded-none mt-2"
              data-testid="mentor-pw-current-input"
            />
          </div>
          <div>
            <Label className="label-eyebrow">New password</Label>
            <Input
              type="password"
              value={pwForm.next}
              onChange={(e) => setPwForm((f) => ({ ...f, next: e.target.value }))}
              className="rounded-none mt-2"
              data-testid="mentor-pw-new-input"
            />
          </div>
          <div>
            <Label className="label-eyebrow">Confirm</Label>
            <Input
              type="password"
              value={pwForm.confirm}
              onChange={(e) => setPwForm((f) => ({ ...f, confirm: e.target.value }))}
              className="rounded-none mt-2"
              data-testid="mentor-pw-confirm-input"
            />
          </div>
        </div>
        {pwError && (
          <div className="mt-3 border border-[#e93e36] bg-[#FDEDED] px-3 py-2 text-sm text-[#7A1F1F]" data-testid="mentor-pw-error">
            {pwError}
          </div>
        )}
        <div className="mt-4 flex items-center gap-3">
          <Button
            onClick={changePassword}
            disabled={pwBusy}
            className="rounded-none bg-[#2596be] hover:bg-[#1e7da3]"
            data-testid="mentor-pw-save-btn"
          >
            <Lock size={13} className="mr-2" /> {pwBusy ? "Updating…" : "Update password"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <Label className="label-eyebrow">{label}</Label>
      <Input className="mt-2 rounded-none" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} />
    </div>
  );
}

function Stat({ label, value, tone, testid }) {
  const color = tone === "red" ? "text-[#A23232]" : "text-[#0A0A0A]";
  return (
    <div className="border border-[#E5E7EB] p-3" data-testid={testid}>
      <div className="label-eyebrow">{label}</div>
      <div className={`font-display text-3xl font-black tracking-tighter mt-1 ${color}`}>{value}</div>
    </div>
  );
}
